import { createSelector, createSlice } from '@reduxjs/toolkit'
import {
  baseReducers,
  getIdOrModelId,
  SBAPI,
  SBSelectRaw,
  SBState,
} from '@supplement-bacon/react-toolkit/utils'
import { initialQuery, TableParams } from '@supplement-bacon/sb-table-filters'
import { FormQuestion } from '../../models/membership/Form'
import { formQuestionSchema } from '../../models/schema'
import {
  createFormQuestion as createFormQuestionAPI,
  deleteFormQuestion as deleteFormQuestionAPI,
  fetchFormQuestions,
  updateFormQuestion as updateFormQuestionAPI,
} from '../../services/membership/FormQuestionService'
import { AppDispatch, RootState } from '../store'

const initialState: SBState<FormQuestion> = {
  isLoading: false,
  error: null,
  items: {},
  ids: [],
  selectedId: undefined,
  query: initialQuery,
}

const slice = createSlice({
  name: 'formQuestion',
  initialState,
  reducers: baseReducers,
})

// Reducer
export default slice.reducer
export const {
  getItemsSuccess: getFormQuestionsSuccess,
  setQuery: setFormQuestionQuery,
  reset: resetFormQuestionState,
  resetQueryAndIds: resetFormQuestionQueryAndIds,
} = slice.actions

/**
 * Selectors
 */

const selectRawItems: SBSelectRaw<
  RootState,
  { [key: number]: FormQuestion }
> = (state: RootState) => state[slice.name].items
const selectRawIds: SBSelectRaw<RootState, number[]> = (state: RootState) =>
  state[slice.name].ids
const selectRawSelectedId: SBSelectRaw<RootState, number | undefined> = (
  state: RootState
) => state[slice.name].selectedId

export const selectFormQuestions = () =>
  createSelector(
    [selectRawItems, selectRawIds],
    (items, ids) => ids.map((id) => items[id]).filter((i) => i)
    // Filter allow to return only non-null elements
  )
export const selectSelectedFormQuestion = () =>
  createSelector([selectRawItems, selectRawSelectedId], (items, id) =>
    id !== undefined ? items[id] : undefined
  )
export const selectFormQuestionById = (id: number) =>
  createSelector([selectRawItems], (items) =>
    items.hasOwnProperty(id) ? items[id] : undefined
  )
export const selectFormQuestionsByIds = (ids: number[]) =>
  createSelector([selectRawItems], (items) =>
    ids.filter((id) => items.hasOwnProperty(id)).map((id) => items[id])
  )

/**
 * Actions
 */

export const setSelectedFormQuestion =
  (formQuestion: FormQuestion | number) => async (dispatch: AppDispatch) =>
    dispatch(
      slice.actions.setSelectedId(getIdOrModelId<FormQuestion>(formQuestion))
    )

export const getFormQuestions = (params: TableParams) =>
  SBAPI.FetchPaginatedDispatched(
    fetchFormQuestions(params),
    [formQuestionSchema],
    slice.actions
  )

export const createFormQuestion = (formQuestion: FormData) =>
  SBAPI.CreateDispatched(
    createFormQuestionAPI(formQuestion),
    formQuestionSchema,
    slice.actions
  )

export const updateFormQuestion = (formQuestion: FormData) =>
  SBAPI.UpdateDispatched<FormQuestion>(
    updateFormQuestionAPI(formQuestion),
    formQuestion,
    slice.actions
  )

export const deleteFormQuestion = (formQuestion: FormQuestion) =>
  SBAPI.DestroyDispatched<FormQuestion>(
    deleteFormQuestionAPI(formQuestion.id),
    formQuestion,
    slice.actions
  )
